import { useEffect, useRef } from "react";
import { Star, Play, CheckCircle2, Quote, ImagePlus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { trackEvent } from "@/lib/analytics";

export const SECTION_TYPES = [
  { type: "hero", label: "Hero Banner" },
  { type: "stats", label: "Stats Strip" },
  { type: "highlights", label: "Highlights" },
  { type: "steps", label: "Steps" },
  { type: "accordion", label: "Accordion / FAQ" },
  { type: "cards_grid", label: "Cards Grid" },
  { type: "feature_block", label: "Feature Block" },
  { type: "profile_grid", label: "Profile Grid" },
  { type: "testimonial_grid", label: "Testimonials" },
  { type: "timeline", label: "Timeline" },
  { type: "pricing_table", label: "Pricing Table" },
  { type: "logo_strip", label: "Logo Strip" },
  { type: "video", label: "Video" },
  { type: "rich_text", label: "Rich Text" },
  { type: "cta_banner", label: "CTA Banner" },
  { type: "comparison_grid", label: "Comparison Grid" },
];

export const typeLabel = (t) => (SECTION_TYPES.find((s) => s.type === t) || {}).label || t;

export function emptyContent(type) {
  switch (type) {
    case "hero": return { headline: "Your headline here", subheadline: "Add a compelling subheadline.", highlights: ["Key point one", "Key point two"], image: "", cta_primary: "Apply Now", cta_secondary: "Download Brochure", cta_primary_link: "", cta_secondary_link: "" };
    case "stats": return { title: "", items: [{ value: "95%", label: "Placement rate" }, { value: "500+", label: "Hiring partners" }, { value: "40+", label: "Guided labs" }, { value: "24/7", label: "Mentor support" }] };
    case "highlights": case "cards_grid": return { title: "Section title", subtitle: "", items: [{ title: "Highlight one", description: "Short description." }] };
    case "steps": case "accordion": return { title: "Section title", subtitle: "", items: [{ title: "Item title", description: "Item description." }] };
    case "feature_block": return { title: "Feature title", description: "Describe this feature.", bullets: ["Benefit one", "Benefit two"] };
    case "profile_grid": return { title: "Meet the team", subtitle: "", items: [{ name: "Name", role: "Role", bio: "" }] };
    case "testimonial_grid": return { title: "What learners say", items: [{ name: "Learner name", role: "Role", quote: "Their quote goes here.", rating: 5 }] };
    case "timeline": return { title: "Timeline", items: [{ label: "Phase 1", title: "Milestone", description: "" }] };
    case "pricing_table": return { title: "Pricing", items: [{ label: "Program fee", amount: "₹0", note: "" }] };
    case "logo_strip": return { items: [{ name: "Partner" }] };
    case "video": return { title: "Watch", description: "", video_url: "" };
    case "rich_text": return { title: "Section title", body: "Write your content here." };
    case "cta_banner": return { headline: "Ready to get started?", subtext: "", cta_primary: "Apply Now", cta_secondary: "Talk to Advisor", cta_primary_link: "", cta_secondary_link: "" };
    case "comparison_grid": return { title: "Compare", columns: ["Basic", "Pro", "Premium"], rows: [{ label: "Feature", values: ["✓", "✓", "✓"] }] };
    default: return {};
  }
}

// ---------- Inline editing primitives ----------

export function EditableText({ value, path, onEdit, className = "", tag: Tag = "span", multiline = false }) {
  return (
    <Tag
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      className={`${className} outline-none rounded-sm cursor-text hover:ring-1 hover:ring-violet-400/70 focus:ring-2 focus:ring-violet-500 transition-shadow`}
      data-edit-path={path}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (!multiline && e.key === "Enter") { e.preventDefault(); e.target.blur(); } if (e.key === "Escape") { e.target.innerText = value || ""; e.target.blur(); } }}
      onBlur={(e) => { const v = e.target.innerText; if (v !== (value || "")) onEdit(path, v); }}
    >
      {value || ""}
    </Tag>
  );
}

function Ed({ v, path, edit, className, tag, multiline }) {
  if (!edit) return <span className={className}>{v}</span>;
  return <EditableText value={v} path={path} onEdit={edit.onEdit} className={className} tag={tag} multiline={multiline} />;
}

function CtaBtn({ label, link, path, edit, solid, btnColor }) {
  const cls = solid
    ? "px-6 py-3 rounded-xl font-bold text-sm inline-block"
    : "px-6 py-3 rounded-xl border font-semibold text-sm inline-block";
  const style = btnColor && solid ? { background: btnColor, color: "#fff" } : {};
  const base = solid ? (btnColor ? "" : "bg-white text-indigo-950") : "border-white/40 text-current";
  const inner = edit
    ? <EditableText value={label} path={path} onEdit={edit.onEdit} className={`${cls} ${base}`} />
    : <span className={`${cls} ${base}`} style={style}>{label}</span>;
  if (edit) return <span style={style} className="rounded-xl inline-block">{inner}</span>;
  return link ? <a href={link} style={style} className="rounded-xl inline-block">{inner}</a> : <span style={style} className="rounded-xl inline-block">{inner}</span>;
}

function EditableImage({ src, path, edit, className, alt = "" }) {
  if (!edit) return src ? <img src={src} alt={alt} className={className} /> : null;
  return (
    <div className={`relative group/img overflow-hidden ${className}`}>
      {src ? <img src={src} alt={alt} className="w-full h-full object-cover cursor-pointer" onClick={(e) => { e.stopPropagation(); edit.onImageClick(path); }} /> : (
        <div className="w-full h-full min-h-[70px] bg-white/10 border-2 border-dashed border-white/40 flex items-center justify-center text-white/70 text-[10px] font-bold cursor-pointer" onClick={(e) => { e.stopPropagation(); edit.onImageClick(path); }}>No image</div>
      )}
      <button type="button" onClick={(e) => { e.stopPropagation(); edit.onImageClick(path); }}
        className="absolute top-2 right-2 z-10 opacity-0 group-hover/img:opacity-100 transition-opacity bg-violet-600 hover:bg-violet-700 text-white text-[10px] font-bold px-2 py-1 rounded-lg flex items-center gap-1 shadow-lg"
        data-testid={`img-edit-${path.replace(/\W+/g, "-")}`}>
        <ImagePlus className="w-3 h-3" /> {src ? "Replace" : "Add"}
      </button>
    </div>
  );
}

// ---------- Style engine ----------

function styleWrap(section, children, padded = true) {
  const st = section.style || {};
  const bg = st.bg_type === "color" && st.bg_color ? { background: st.bg_color }
    : st.bg_type === "gradient" ? { background: `linear-gradient(135deg, ${st.bg_gradient_from || "#4f46e5"}, ${st.bg_gradient_to || "#7c3aed"})` }
    : st.bg_type === "image" && st.bg_image ? { backgroundImage: `url(${st.bg_image})`, backgroundSize: "cover", backgroundPosition: "center" } : {};
  const pad = !padded ? "" : st.padding === "compact" ? "py-2" : st.padding === "spacious" ? "py-10" : "";
  const cls = [
    pad,
    st.align === "center" ? "text-center" : "",
    st.width === "contained" ? "max-w-6xl mx-auto my-5 rounded-3xl overflow-hidden" : "",
  ].filter(Boolean).join(" ");
  return <div style={bg} className={cls} data-tone={st.text_tone || ""} data-tsize={st.text_size || ""}>{children}</div>;
}

const H = ({ children, sub, edit }) => (
  <div className="mb-6">
    <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">
      <Ed v={children} path="title" edit={edit} />
    </h2>
    {(sub != null) && <p className="text-sm text-slate-500 mt-1"><Ed v={sub} path="subtitle" edit={edit} multiline /></p>}
  </div>
);

// ---------- Renderer ----------

export function SectionRenderer({ section, slug, editable = false, onEdit, onImageClick }) {
  const c = section.content || {};
  const items = Array.isArray(c.items) ? c.items : [];
  const st = section.style || {};
  const edit = editable ? { onEdit, onImageClick } : null;

  switch (section.layout_type) {
    case "hero":
      return styleWrap(section, (
        <div className="relative overflow-hidden bg-gradient-to-br from-indigo-950 via-violet-900 to-indigo-900 text-white">
          {(c.image || edit) && (
            edit
              ? <EditableImage src={c.image} path="image" edit={edit} className="absolute inset-0 w-full h-full opacity-25" />
              : <img src={c.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          )}
          <div className="relative max-w-6xl mx-auto px-6 py-16 sm:py-24">
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight max-w-2xl leading-tight">
              <Ed v={c.headline || "Untitled"} path="headline" edit={edit} />
            </h1>
            <p className="mt-4 text-indigo-100/90 max-w-xl"><Ed v={c.subheadline} path="subheadline" edit={edit} multiline /></p>
            {(c.highlights || []).length > 0 && (
              <div className="flex flex-wrap gap-2.5 mt-6">
                {c.highlights.filter(Boolean).map((h, i) => (
                  <span key={i} className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-white/10 border border-white/20 backdrop-blur">
                    <Ed v={h} path={`highlights.${i}`} edit={edit} />
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-8">
              {c.cta_primary && <CtaBtn label={c.cta_primary} link={c.cta_primary_link} path="cta_primary" edit={edit} solid btnColor={st.btn_color} />}
              {c.cta_secondary && <CtaBtn label={c.cta_secondary} link={c.cta_secondary_link} path="cta_secondary" edit={edit} />}
            </div>
          </div>
        </div>
      ), false);
    case "stats":
      return styleWrap(section, (
        <div className="bg-violet-700 text-white" style={st.bg_type ? {} : undefined} data-tone="light">
          <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {items.map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-extrabold"><Ed v={s.value} path={`items.${i}.value`} edit={edit} /></p>
                <p className="text-xs text-violet-200 mt-1 uppercase tracking-wider"><Ed v={s.label} path={`items.${i}.label`} edit={edit} /></p>
              </div>
            ))}
          </div>
        </div>
      ), false);
    case "highlights": case "cards_grid":
      return styleWrap(section, (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <H sub={c.subtitle} edit={edit} base="content-root">{c.title || section.name}</H>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-violet-300 hover:shadow-md transition-all">
                <CheckCircle2 className="w-5 h-5 text-violet-600 mb-2.5" />
                <p className="font-bold text-slate-900 text-sm"><Ed v={it.title} path={`items.${i}.title`} edit={edit} /></p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed"><Ed v={it.description} path={`items.${i}.description`} edit={edit} multiline /></p>
              </div>
            ))}
          </div>
        </section>
      ));
    case "steps":
      return styleWrap(section, (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <H sub={c.subtitle} edit={edit} base="content-root">{c.title || section.name}</H>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {items.map((it, i) => (
              <div key={i} className="relative rounded-2xl bg-slate-50 border border-slate-200 p-5">
                <span className="w-8 h-8 rounded-lg bg-violet-600 text-white text-sm font-extrabold flex items-center justify-center">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-bold text-slate-900 text-sm mt-3"><Ed v={it.title} path={`items.${i}.title`} edit={edit} /></p>
                <p className="text-xs text-slate-500 mt-1"><Ed v={it.description} path={`items.${i}.description`} edit={edit} multiline /></p>
              </div>
            ))}
          </div>
        </section>
      ));
    case "accordion":
      return styleWrap(section, (
        <section className="max-w-4xl mx-auto px-6 py-12 w-full">
          <H sub={c.subtitle} edit={edit} base="content-root">{c.title || section.name}</H>
          <Accordion type="single" collapsible>
            {items.map((it, i) => (
              <AccordionItem key={i} value={`s-${section.id}-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-slate-800">
                  <Ed v={it.title} path={`items.${i}.title`} edit={edit} />
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  <Ed v={it.description} path={`items.${i}.description`} edit={edit} multiline />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ));
    case "feature_block":
      return styleWrap(section, (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <div className="rounded-3xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100 p-8 sm:p-10">
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 mb-4"><Ed v={c.title || section.name} path="title" edit={edit} /></h2>
            <p className="text-slate-600 max-w-2xl leading-relaxed"><Ed v={c.description} path="description" edit={edit} multiline /></p>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {(c.bullets || []).filter(Boolean).map((b, i) => (
                <p key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
                  <Ed v={b} path={`bullets.${i}`} edit={edit} />
                </p>
              ))}
            </div>
          </div>
        </section>
      ));
    case "profile_grid":
      return styleWrap(section, (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <H sub={c.subtitle} edit={edit} base="content-root">{c.title || section.name}</H>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map((it, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
                {it.image ? (
                  <EditableImage src={it.image} path={`items.${i}.image`} edit={edit} className="w-14 h-14 rounded-full mx-auto" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-violet-100 text-violet-700 font-extrabold text-lg flex items-center justify-center mx-auto">{(it.name || "?").slice(0, 1)}</div>
                )}
                <p className="font-bold text-slate-900 text-sm mt-3"><Ed v={it.name} path={`items.${i}.name`} edit={edit} /></p>
                <p className="text-xs text-violet-600 font-semibold"><Ed v={it.role} path={`items.${i}.role`} edit={edit} /></p>
                <p className="text-xs text-slate-500 mt-2"><Ed v={it.bio} path={`items.${i}.bio`} edit={edit} multiline /></p>
              </div>
            ))}
          </div>
        </section>
      ));
    case "testimonial_grid":
      return styleWrap(section, (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <H edit={edit} base="content-root">{c.title || section.name}</H>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {items.map((it, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col">
                <Quote className="w-5 h-5 text-violet-300 mb-2" />
                <p className="text-sm text-slate-600 leading-relaxed flex-1"><Ed v={it.quote} path={`items.${i}.quote`} edit={edit} multiline /></p>
                <div className="flex gap-0.5 mt-3">{Array.from({ length: it.rating || 5 }).map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                <p className="font-bold text-slate-900 text-sm mt-2"><Ed v={it.name} path={`items.${i}.name`} edit={edit} /></p>
                <p className="text-xs text-slate-400"><Ed v={it.role} path={`items.${i}.role`} edit={edit} /></p>
              </div>
            ))}
          </div>
        </section>
      ));
    case "timeline":
      return styleWrap(section, (
        <section className="max-w-4xl mx-auto px-6 py-12 w-full">
          <H edit={edit} base="content-root">{c.title || section.name}</H>
          <div className="border-l-2 border-violet-200 ml-3 flex flex-col gap-6">
            {items.map((it, i) => (
              <div key={i} className="relative pl-6">
                <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-violet-600 border-4 border-violet-100" />
                <p className="text-xs font-bold text-violet-600 uppercase tracking-wider"><Ed v={it.label} path={`items.${i}.label`} edit={edit} /></p>
                <p className="font-bold text-slate-900 text-sm mt-0.5"><Ed v={it.title} path={`items.${i}.title`} edit={edit} /></p>
                <p className="text-xs text-slate-500 mt-1"><Ed v={it.description} path={`items.${i}.description`} edit={edit} multiline /></p>
              </div>
            ))}
          </div>
        </section>
      ));
    case "pricing_table":
      return styleWrap(section, (
        <section className="max-w-4xl mx-auto px-6 py-12 w-full">
          <H edit={edit} base="content-root">{c.title || section.name}</H>
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
            {items.map((it, i) => (
              <div key={i} className="flex items-center justify-between px-6 py-4">
                <div>
                  <p className="font-semibold text-slate-800 text-sm"><Ed v={it.label} path={`items.${i}.label`} edit={edit} /></p>
                  <p className="text-xs text-slate-400"><Ed v={it.note} path={`items.${i}.note`} edit={edit} /></p>
                </div>
                <p className="font-extrabold text-violet-700"><Ed v={it.amount} path={`items.${i}.amount`} edit={edit} /></p>
              </div>
            ))}
          </div>
        </section>
      ));
    case "logo_strip":
      return styleWrap(section, (
        <div className="border-y border-slate-100 bg-slate-50/60">
          <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {items.map((it, i) => (
              it.logo || edit ? (
                <EditableImage key={i} src={it.logo} path={`items.${i}.logo`} edit={edit} className="h-7 rounded" alt={it.name} />
              ) : null
            ))}
            {items.map((it, i) => (
              !it.logo ? <span key={`n-${i}`} className="text-sm font-bold text-slate-400 uppercase tracking-wide"><Ed v={it.name} path={`items.${i}.name`} edit={edit} /></span> : null
            ))}
          </div>
        </div>
      ), false);
    case "video":
      return styleWrap(section, (
        <section className="max-w-4xl mx-auto px-6 py-12 w-full">
          <H edit={edit} base="content-root">{c.title || section.name}</H>
          <div className="aspect-video rounded-2xl bg-slate-900 flex items-center justify-center">
            <span className="w-16 h-16 rounded-full bg-white/15 border border-white/30 flex items-center justify-center"><Play className="w-6 h-6 text-white fill-white" /></span>
          </div>
          <p className="text-sm text-slate-500 mt-3"><Ed v={c.description} path="description" edit={edit} multiline /></p>
        </section>
      ));
    case "rich_text":
      return styleWrap(section, (
        <section className="max-w-4xl mx-auto px-6 py-12 w-full">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 mb-4"><Ed v={c.title || section.name} path="title" edit={edit} /></h2>
          {edit ? (
            <p className="text-slate-600 leading-relaxed"><Ed v={c.body} path="body" edit={edit} multiline /></p>
          ) : (
            (c.body || "").split("\n").filter(Boolean).map((p, i) => <p key={i} className="text-slate-600 leading-relaxed mb-3">{p}</p>)
          )}
        </section>
      ));
    case "cta_banner":
      return styleWrap(section, (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <div className="rounded-3xl bg-gradient-to-r from-violet-700 to-indigo-800 text-white px-8 py-12 text-center" data-tone="light">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight"><Ed v={c.headline || "Ready to get started?"} path="headline" edit={edit} /></h2>
            <p className="text-violet-200 mt-2 max-w-xl mx-auto text-sm"><Ed v={c.subtext} path="subtext" edit={edit} multiline /></p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {c.cta_primary && <CtaBtn label={c.cta_primary} link={c.cta_primary_link} path="cta_primary" edit={edit} solid btnColor={st.btn_color} />}
              {c.cta_secondary && <CtaBtn label={c.cta_secondary} link={c.cta_secondary_link} path="cta_secondary" edit={edit} />}
            </div>
          </div>
        </section>
      ));
    case "comparison_grid": {
      const cols = c.columns || [];
      return styleWrap(section, (
        <section className="max-w-4xl mx-auto px-6 py-12 w-full overflow-x-auto">
          <H edit={edit} base="content-root">{c.title || section.name}</H>
          <table className="w-full text-sm border border-slate-200 rounded-2xl overflow-hidden">
            <thead><tr className="bg-violet-700 text-white"><th className="px-4 py-3 text-left font-semibold"></th>
              {cols.map((col, i) => <th key={i} className="px-4 py-3 font-bold"><Ed v={col} path={`columns.${i}`} edit={edit} /></th>)}</tr></thead>
            <tbody>
              {(c.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold text-slate-700"><Ed v={r.label} path={`rows.${i}.label`} edit={edit} /></td>
                  {(r.values || []).map((v, j) => <td key={j} className="px-4 py-3 text-center text-slate-600"><Ed v={v} path={`rows.${i}.values.${j}`} edit={edit} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ));
    }
    default:
      return null;
  }
}

function TrackedSection({ section, slug, children }) {
  const ref = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    if (!slug || !ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !fired.current) {
        fired.current = true;
        trackEvent("section_view", { slug, section: section.name });
      }
    }, { threshold: 0.35 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [slug, section.name]);
  return <div ref={ref} data-section={section.name}>{children}</div>;
}

export default function SectionsRenderer({ sections, slug }) {
  const visible = (sections || []).filter((s) => s.visible !== false);
  if (!visible.length) return null;
  return (
    <div data-testid="program-sections">
      {visible.map((s) => (
        <TrackedSection key={s.id} section={s} slug={slug}>
          <SectionRenderer section={s} slug={slug} />
        </TrackedSection>
      ))}
    </div>
  );
}
